import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

// Strong credentials - change these before production!
const MAINTENANCE_USERNAME = 'rarifex_admin';
const MAINTENANCE_PASSWORD = 'Rf#2025!MilanoLaunch$Xk9';

export default function MaintenancePage({ onBypass }) {
  const [showLogin, setShowLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Simulate network delay for security
    setTimeout(() => {
      if (username === MAINTENANCE_USERNAME && password === MAINTENANCE_PASSWORD) {
        localStorage.setItem('rarifex_maintenance_bypass', 'true');
        onBypass();
      } else {
        setError('Credenziali non valide');
        setPassword('');
      }
      setLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 overflow-hidden relative">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-64 h-64 rounded-full"
            style={{
              background: i % 2 === 0 
                ? 'radial-gradient(circle, rgba(0,128,128,0.15) 0%, transparent 70%)'
                : 'radial-gradient(circle, rgba(218,165,32,0.1) 0%, transparent 70%)',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, 30, 0],
              y: [0, -30, 0],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      <div className="relative z-10 text-center max-w-lg mx-auto">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          {/* Tricolore */}
          <div className="flex justify-center mb-4">
            <div className="flex rounded overflow-hidden h-6 w-16">
              <div className="flex-1 bg-[#009246]" />
              <div className="flex-1 bg-white" />
              <div className="flex-1 bg-[#CE2B37]" />
            </div>
          </div>
          <div className="mb-4">
            <span className="text-4xl font-bold text-white tracking-tight">RARIFEX</span>
          </div>
          <p className="text-amber-400 font-serif italic text-lg">Tradizione, Passione, Autenticità</p>
          <p className="text-slate-400 text-sm mt-2">L'eccellenza artigiana italiana</p>
        </motion.div>

        {/* Main content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl"
        >
          {!showLogin ? (
            <>
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="inline-block mb-6"
              >
                <Sparkles className="w-16 h-16 text-amber-400" />
              </motion.div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Coming Soon
              </h1>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <div className="flex items-center gap-2 text-slate-400">
                  <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
                  <span>In fase di sviluppo</span>
                </div>
              </div>

              <div className="mt-10 pt-6 border-t border-white/10">
                <button
                  onClick={() => setShowLogin(true)}
                  className="text-slate-500 hover:text-slate-300 text-sm flex items-center gap-2 mx-auto transition-colors"
                >
                  <Lock className="w-4 h-4" />
                  Accesso Team
                </button>
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <Lock className="w-12 h-12 text-teal-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Accesso Riservato</h2>
              <p className="text-slate-400 mb-6">Inserisci le credenziali per accedere</p>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Input
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                    disabled={loading}
                  />
                </div>
                
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 pr-10"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-red-400 text-sm"
                  >
                    {error}
                  </motion.p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !username || !password}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Accedi'
                  )}
                </Button>
              </form>

              <button
                onClick={() => {
                  setShowLogin(false);
                  setError('');
                  setUsername('');
                  setPassword('');
                }}
                className="mt-4 text-slate-500 hover:text-slate-300 text-sm transition-colors"
              >
                ← Torna indietro
              </button>
            </motion.div>
          )}
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8 text-slate-500 text-sm"
        >
          © 2026 Rarifex. Tutti i diritti riservati.
        </motion.p>
      </div>
    </div>
  );
}
